import { notificationUrl, type RoutableNotification } from "./notificationRouting";

export const NOTIFICATION_INBOX_LIMIT = 30;

export type NotificationPriority = "low" | "normal" | "high" | "urgent";

export type NotificationVisualType =
  | "message"
  | "booking"
  | "payment"
  | "vehicle"
  | "safety"
  | "listing"
  | "community"
  | "info";

export type NotificationMetadata = {
  in_app_silent?: boolean;
  event_type?: string;
  scenario_id?: string;
  preference_key?: string;
  role?: string;
  action_url?: string;
  notification_contract_version?: number;
  [key: string]: unknown;
};

export interface NotificationInboxItem extends RoutableNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  created_at: string;
  priority: NotificationPriority;
  metadata: NotificationMetadata;
}

export function normalizeNotificationItem(value: unknown): NotificationInboxItem {
  const row = isRecord(value) ? value : {};
  const metadata = normalizeMetadata(row.metadata);
  const type = text(row.type, "info");
  const actionUrl = text(row.action_url, null) ?? text(metadata.action_url, null);

  return {
    id: text(row.id, "") ?? "",
    title: text(row.title, "VoltyShare") ?? "VoltyShare",
    message: text(row.message, "") ?? "",
    type,
    read: Boolean(row.read),
    created_at: text(row.created_at, new Date(0).toISOString()) ?? new Date(0).toISOString(),
    related_booking_id: text(row.related_booking_id, null),
    related_listing_id: text(row.related_listing_id, null),
    action_url: actionUrl,
    priority: normalizePriority(row.priority),
    metadata,
  };
}

export function normalizeNotificationItems(values: unknown[]): NotificationInboxItem[] {
  return values.map(normalizeNotificationItem).filter((item) => item.id);
}

export function shouldShowInInbox(item: NotificationInboxItem) {
  return !item.metadata.in_app_silent;
}

export function shouldShowForegroundToast(item: NotificationInboxItem) {
  return shouldShowInInbox(item);
}

export function shouldPlayNotificationSound(item: NotificationInboxItem) {
  return shouldShowForegroundToast(item) && item.priority !== "low";
}

export function shouldCountAsUnreadMessage(item: NotificationInboxItem) {
  return shouldShowInInbox(item) && item.type === "message" && !item.read;
}

export function isPriorityNotification(item: NotificationInboxItem) {
  return item.priority === "high" || item.priority === "urgent";
}

export function notificationActionUrl(item: NotificationInboxItem) {
  return notificationUrl(item);
}

export function notificationVisualType(item: NotificationInboxItem): NotificationVisualType {
  const eventType = item.metadata.event_type || item.metadata.scenario_id || "";
  if (item.type === "message") return "message";
  if (item.type === "booking") return "booking";
  if (item.type === "payment") return "payment";
  if (item.type === "vehicle") return "vehicle";
  if (item.type === "safety" || eventType.includes("dispute")) return "safety";
  if (eventType.startsWith("listing_")) return "listing";
  if (eventType.startsWith("station_contribution")) return "community";
  return "info";
}

export function mergeNotificationInbox(
  current: NotificationInboxItem[],
  next: NotificationInboxItem[],
  limit = NOTIFICATION_INBOX_LIMIT,
) {
  const byId = new Map<string, NotificationInboxItem>();
  for (const item of [...next, ...current]) {
    if (!item.id || !shouldShowInInbox(item)) continue;
    byId.set(item.id, item);
  }
  return Array.from(byId.values()).sort(byNewestFirst).slice(0, limit);
}

export function unreadNotificationCount(items: NotificationInboxItem[]) {
  return items.filter((item) => !item.read).length;
}

export function byNewestFirst(a: NotificationInboxItem, b: NotificationInboxItem) {
  return timestamp(b.created_at) - timestamp(a.created_at);
}

function normalizePriority(value: unknown): NotificationPriority {
  return value === "low" || value === "normal" || value === "high" || value === "urgent"
    ? value
    : "normal";
}

function normalizeMetadata(value: unknown): NotificationMetadata {
  if (!isRecord(value)) return {};
  return { ...value } as NotificationMetadata;
}

function text(value: unknown, fallback: string): string;
function text(value: unknown, fallback: null): string | null;
function text(value: unknown, fallback: string | null) {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed || fallback;
}

function timestamp(value: string) {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
